/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { Suspense, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';

import { CartExtendToggle } from '@/components/cart/cart-extend-toggle';
import KitQuantityPicker from '@/components/cart/kit-quantity-picker';
import CartDeliveryEstimate from '@/components/delivery-estimate/cart-delivery-estimate';
import BackorderNotice from '@/components/delivery-estimate/backorder-notice';
import { Link } from '@/components/link';
import ProductPrice from '@/components/product-price';
import CartLinePromoCallouts, {
    DeferredCartLinePromoCallouts,
} from '@/components/product-price/cart-line-promo-callouts';
import { Typography } from '@/components/typography';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { aggregateKitAtpFields } from '@/lib/atp/atp-fields';
import { formatCurrency } from '@/lib/currency';
import { createProductUrl, type EnrichedProductItem } from '@/lib/product/product-utils';
import { getKitDisplaySummary, getKitGroupPricing, type KitGroup, type KitProductFields } from '@/lib/kit-utils';
import KitGroupImage from '@/components/product-kit/kit-group-image';
import { KitItemVariations } from '@/components/product-kit/kit-item-variations';
import { getFirstAtpUnavailableComponent } from '@/lib/cart/kit-availability';
import { isPliPriceUnavailable } from '@/lib/cart/price-availability';
import UnavailablePrice from '@/components/product-price/unavailable-price';

interface KitGroupCardProps {
    /** The basket-level kit group descriptor (`c_kitGroups[]`). */
    group: KitGroup;
    /** The exploded component line items belonging to this kit instance, in basket order. */
    items: EnrichedProductItem[];
    /**
     * Same Save-for-later / Remove render prop the standard `<ProductItem>`
     * receives, so a kit row carries the identical secondary actions. It is
     * invoked with a representative line item anchored on the kit's first
     * component (Remove cascades to the whole kit server-side).
     */
    secondaryActions?: (product: EnrichedProductItem) => ReactElement | undefined;
    /** Optional kit-master product payload used for the hero thumbnail. */
    masterProduct?: ShopperProducts.schemas['Product'];
    /**
     * SSF-390 Issue 6 — Promotion details keyed by `promotionId`. Passed
     * through to `<CartLinePromoCallouts>` so the kit callout can prefer the
     * richer customer-facing `offerMsg` / `calloutMsg` from the Shopper
     * Promotions API. Optional — the callout still renders on the merchant-
     * authored `itemText` fallback when the map is missing.
     */
    promotions?: Record<string, ShopperPromotions.schemas['Promotion']>;
    /**
     * Non-critical promotions data promise. When provided, promo callouts on
     * the kit row stream in under their own Suspense boundary so the card
     * never waits on the Promotions API — same contract as `<ProductItem>`.
     */
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
}

/**
 * CART-19 — renders a kit/package as a SINGLE cart line item, matching the
 * standard line-item treatment (image, name, roll-up price, Extend toggle,
 * Save-for-later / Remove, a "Product Includes" list, and a quantity picker) so
 * all kits render consistently with the rest of the cart.
 *
 * The backend explodes a kit master into one PLI per component (sharing one
 * `partOfKitNumber`); this card collapses those PLIs back into one visual line:
 *   - image / name      → the master (via the first component + `group.name`)
 *   - price             → `group.lineTotal` (Σ component adjusted prices)
 *   - quantity          → kit instance count, edited via `<KitQuantityPicker>`
 *                         which scales every component server-side
 *   - Extend            → keyed on the master product id; one offer per kit
 *   - Product Includes  → the component list (read-only; the kit is atomic)
 *   - Remove            → cascades to every component (grouped by partOfKitNumber)
 *
 * The underlying explosion / roll-up pricing model is intentionally left
 * unchanged — this is a presentation-only collapse.
 */
export default function KitGroupCard({
    group,
    items,
    secondaryActions,
    masterProduct,
    promotions,
    promotionsPromise,
}: KitGroupCardProps): ReactElement | null {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();
    // "Product Includes" list defaults to expanded, matching <BundledProductItems> (CART-02 spec).
    const [includesExpanded, setIncludesExpanded] = useState(true);

    const anchor = items[0];
    const anchorItemId = group.itemIds[0] ?? anchor?.itemId ?? '';
    const quantity = group.quantity || 1;

    // SSF-413 follow-up — surface component-level ATP unavailability at the
    // kit-card level. Kit components render exclusively inside this collapsed
    // card (see `product-items-list/index.tsx` — PLIs carrying
    // `c_partOfKitNumber` are skipped as standalone `<ProductItem>` rows), so
    // the row-level ATP-unavailable rendering from `<CartDeliveryEstimate>`
    // never fires for a kit. Without this the Proceed-to-Checkout gate
    // correctly disables the CTA (a component PLI with `c_atpIsAvailable=false`
    // trips `isCheckoutBlockedCartItem`) but the shopper has no visible
    // explanation. A kit is atomic — any unavailable component makes the whole
    // kit unbuyable — so we anchor the alert on the first unavailable
    // component and hand it to the SAME `<CartDeliveryEstimate>` regular rows
    // use, avoiding a bespoke banner or icon treatment.
    const unavailableKitComponent = getFirstAtpUnavailableComponent(items);

    // SSF-390 Issue 6 — aggregate the kit-level pre-promo total and promo
    // callouts from the component PLIs. `group.lineTotal` on the SCAPI response
    // is already the DISCOUNTED roll-up, so on its own the card can't show the
    // strikethrough or the "Save $X" message. `getKitGroupPricing` walks each
    // component's `basePrice × quantity` (pre-promo) and dedupes their
    // `priceAdjustments[]` by promotion so the card renders one strikethrough
    // + one callout per applied promo instead of N repeats.
    const pricing = useMemo(
        () => getKitGroupPricing(group, items, masterProduct as KitProductFields | undefined),
        [group, items, masterProduct]
    );

    // Synthetic basket-line shape so <ProductPrice> prices the kit roll-up
    // through the same getPriceData basket-item branch every non-kit row
    // uses. quantity: 1 because both totals already reflect the full kit
    // instance count (group.quantity), so no further multiplication is needed.
    const kitPriceItem = useMemo(
        () =>
            ({
                itemId: group.kitNumber,
                basePrice: pricing.originalLineTotal,
                price: pricing.lineTotal,
                quantity: 1,
            }) as ShopperBasketsV2.schemas['ProductItem'],
        [group.kitNumber, pricing.originalLineTotal, pricing.lineTotal]
    );

    // Aggregate the per-component ATP fields into a virtual kit line item so
    // `<BackorderNotice>` can detect a backordered kit using the exact
    // same resolver the loose product rows use. Kits ship as a single unit,
    // so this rolls up backorder status across every component (parity with
    // the server-side `AtpKitAggregator.aggregateKitDates`).
    const kitDeliveryItem = useMemo(() => aggregateKitAtpFields(items), [items]);

    // A representative line item for the kit, used by the shared secondary
    // actions (Save-for-later / Remove) and the Extend toggle. It is anchored on
    // the first component PLI (so Remove → cascade) but presents the MASTER
    // product id + name (so Extend looks up the master's offer and one offer
    // shows per kit, matching legacy `extendOBOHelpers` kit dedup).
    const representativeItem = useMemo<EnrichedProductItem>(
        () =>
            ({
                ...anchor,
                itemId: anchorItemId,
                productId: group.masterId ?? anchor?.productId,
                productName: group.name ?? anchor?.productName ?? anchor?.name,
                quantity,
            }) as EnrichedProductItem,
        [anchor, anchorItemId, group.masterId, group.name, quantity]
    );

    // Keep includes quantity aligned with the shopper-facing kit quantity row
    // so cart/checkout/confirmation don't disagree on the same kit.
    const kitDisplaySummary = getKitDisplaySummary(group, items);
    const includesItems = useMemo(
        () =>
            items.map((item) => {
                return {
                    key: item.itemId,
                    label: item.productName || item.name || item.productId || '',
                    quantity: kitDisplaySummary.quantity,
                };
            }),
        [items, kitDisplaySummary.quantity]
    );

    if (items.length === 0) {
        return null;
    }

    const title = group.name || anchor?.productName || anchor?.name || '';
    const visualItem = masterProduct ? ({ ...masterProduct, productName: title } as EnrichedProductItem) : anchor;
    const perKitTotal = quantity > 0 ? group.lineTotal / quantity : group.lineTotal;
    // A kit is atomic — any component with `c_priceIsAvailable=false` (see
    // `PricingAvailabilityHelper`) makes the whole roll-up ($group.lineTotal)
    // a misleading zero because the cartridge deliberately writes literal 0
    // on unpriced PLIs to protect basket totals from `Money.NOT_AVAILABLE`.
    // Swap the currency render (and suppress the pre-promo strikethrough,
    // which would also be a lie) for the shared `<UnavailablePrice>` label
    // used on PDP so kit rows read consistently with regular PLI rows.
    const kitPriceUnavailable = items.some(isPliPriceUnavailable);
    // SSF-390 Issue 7 — kit title routes to the kit master PDP. Anchor on
    // `group.masterId` (the resolved virtual master id), not the first
    // component PLI, so the shopper lands on the same PDP that owns the
    // kit's roll-up price and Extend offer. Fall back to `#` via
    // `createProductUrl` when the master id is missing (empty kit — should
    // never render, but keeps the anchor navigable).
    const kitMasterUrl = createProductUrl(group.masterId ?? undefined, null, 'color', null, title);

    return (
        <div
            data-testid={`sf-kit-group-${group.masterId ?? group.kitNumber}`}
            className="relative overflow-hidden px-0 py-6 md:px-0 md:py-8">
            <div className="grid min-w-0 grid-cols-[112px_minmax(0,1fr)] gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:gap-6">
                <div className="flex flex-shrink-0 items-start justify-center">
                    <Link to={kitMasterUrl} aria-label={title} className="block">
                        <KitGroupImage
                            productItem={visualItem}
                            title={title}
                            containerClassName="w-28 md:w-40 aspect-square overflow-hidden rounded-sm"
                        />
                    </Link>
                </div>

                <div className="min-w-0 flex-1 space-y-3">
                    <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px] md:gap-6">
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold leading-6 text-foreground md:text-lg">
                                <Link to={kitMasterUrl} className="hover:underline">
                                    {title}
                                </Link>
                            </h3>
                            {kitPriceUnavailable ? (
                                <div className="mt-1.5 flex flex-wrap items-baseline gap-2">
                                    <UnavailablePrice labelForA11y={title} className="text-lg leading-7" />
                                </div>
                            ) : (
                                <div className="mt-1.5 flex flex-wrap items-center gap-y-1.5 gap-x-2">
                                    <ProductPrice
                                        product={kitPriceItem}
                                        currency={currency}
                                        type="total"
                                        quantity={1}
                                        hidePromo
                                        hasDiscount={pricing.hasSavings}
                                        labelForA11y={title}
                                        className="flex flex-wrap items-center gap-2"
                                        currentPriceProps={{
                                            className: 'text-sm font-normal leading-5 relative',
                                        }}
                                        listPriceProps={{
                                            className:
                                                'text-muted-foreground text-sm font-normal leading-5 relative line-through',
                                        }}
                                    />
                                    <Suspense
                                        fallback={
                                            <CartLinePromoCallouts priceAdjustments={pricing.promoAdjustments} />
                                        }>
                                        <DeferredCartLinePromoCallouts
                                            priceAdjustments={pricing.promoAdjustments}
                                            promotions={promotions}
                                            promotionsPromise={promotionsPromise}
                                        />
                                    </Suspense>
                                </div>
                            )}
                            {/* Item ID — mirrors the standard <ProductItem>
                                "Item · <productId>" row so kits render the
                                same identifier line as every other cart line.
                                Anchor on the kit master id (`group.masterId`);
                                fall back to the anchor component's productId
                                so the row still renders on the (unlikely)
                                stale-basket case where the master id is
                                missing but a component id is present. */}
                            {(group.masterId ?? anchor?.productId) ? (
                                <div
                                    className="mt-1.5 text-xs text-muted-foreground md:text-sm"
                                    data-testid="sf-kit-group-item-id">
                                    {t('attributes.itemId')} · {group.masterId ?? anchor?.productId}
                                </div>
                            ) : null}
                            <KitItemVariations items={items} masterProduct={masterProduct} kitTitle={title} />
                        </div>

                        <div className="grid content-start justify-items-start gap-3 md:justify-items-end md:gap-4">
                            <div className="w-full md:w-[112px]">
                                <KitQuantityPicker anchorItemId={anchorItemId} value={quantity} />
                            </div>
                            {quantity > 1 && !kitPriceUnavailable ? (
                                <Typography variant="small" className="text-xs text-muted-foreground">
                                    {`${formatCurrency(perKitTotal, i18n.language, currency)}/each`}
                                </Typography>
                            ) : null}
                        </div>
                    </div>

                    <div className="mt-3 w-full space-y-3">
                        {secondaryActions ? <div className="w-full">{secondaryActions(representativeItem)}</div> : null}

                        {/* SSF-413 follow-up — reuse the exact `<CartDeliveryEstimate>`
                            regular rows render for the 'unavailable' variant so kits
                            and non-kit rows read identically (same copy, same red
                            typography, no bespoke icon or banner). Anchored on the
                            first unavailable component; any component being
                            unavailable makes the whole kit unbuyable — matching the
                            checkout gate's reasoning. */}
                        {unavailableKitComponent && (
                            <div data-testid="sf-kit-group-atp-unavailable">
                                <CartDeliveryEstimate productItem={unavailableKitComponent} />
                            </div>
                        )}

                        <BackorderNotice productItem={kitDeliveryItem} />

                        {/* Extend protection-plan toggle, keyed on the kit master.
                            Self-hides unless the cart-route offers map carries an
                            offer for the master product id. */}
                        <CartExtendToggle product={representativeItem} className="w-full" />

                        {/* "Product Includes" — the kit's components (read-only),
                            rendered with the same collapsible treatment as a
                            standard bundle's <BundledProductItems> so kits and
                            SCAPI bundles look identical in the cart. */}
                        {includesItems.length > 0 ? (
                            <div className="mt-2 space-y-2" data-testid="sf-kit-product-includes">
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between text-sm font-medium text-foreground"
                                    aria-expanded={includesExpanded}
                                    onClick={() => setIncludesExpanded((prev) => !prev)}>
                                    <span>{`${t('bundle.productIncludes')}:`}</span>
                                    {includesExpanded ? (
                                        <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                                    )}
                                </button>

                                {includesExpanded ? (
                                    <div className="space-y-2 pl-2">
                                        {includesItems.map((item) => (
                                            <div key={item.key} className="space-y-0.5">
                                                <div className="text-sm text-foreground">{item.label}</div>
                                                <div className="text-sm text-muted-foreground">
                                                    {`${t('attributes.quantity')} ${item.quantity}`}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
